import type { VV } from '@semios/app-platform-value-type-definitions'
import { DashboardMessage } from 'App/dashboards/_components/DashboardMessage'
import { downloadCsv } from 'App/dashboards/_utils/dashboardDownloads'
import { getTrapsForProperty } from 'App/dashboards/_utils/getTrapsForProperty'
import { trapCatchKeyForInsect } from 'App/dashboards/_utils/pestValueTypeKeys'
import { sliceTrapTimeseries } from 'App/dashboards/_utils/sliceTrapTimeseries'
import { useTrapCatchPoints } from 'App/dashboards/_utils/useTrapCatchPoints'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import { colors, dashboardColors } from 'settings/colors'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { WidgetCard } from './WidgetCard'

const formatCount = (n: number | null): string => (n === null ? '-' : String(n))
// CSV uses an empty cell for "no data" rather than the on-screen "-" placeholder: a leading "-"
// trips the CSV formula-injection guard (it becomes "'-"), and a blank cell is the standard
// spreadsheet representation of a missing value (still distinct from a real 0).
const formatCountForCsv = (n: number | null): string => (n === null ? '' : String(n))

// Five equal-width, left-aligned columns (table-layout: fixed makes the 20% widths stick).
const headerCss = {
  textAlign: 'left' as const,
  width: '20%',
  padding: '12px 16px',
  fontSize: 14,
  fontWeight: 600,
  color: dashboardColors.columnHeaderText,
}

const cellCss = {
  textAlign: 'left' as const,
  width: '20%',
  padding: '12px 16px',
  fontSize: 14,
  // Fixed table layout locks the column width, so wrap long trap/block names (incl. unbroken
  // strings) onto the next line instead of overflowing.
  overflowWrap: 'break-word' as const,
  borderTop: `1px solid ${colors.grey200}`,
}

const todayHeaderCss = { ...headerCss, background: dashboardColors.todayColumnBg }
const todayCellCss = { ...cellCss, background: dashboardColors.todayColumnBg }

export const HistoricalTrapCounts = ({
  insectId,
  pestName,
  propertyId,
  hasPermission,
  refreshToken,
}: {
  insectId: number
  // English pest name (config.name), used to prefix the CSV filename the same way the chart's JPG
  // export does (`<pest> <document> - <property>`).
  pestName: string
  // Driven by the board's per-dashboard selection (PestDashboard), not the shared filter context.
  propertyId: number | null
  // Split from the layer flag by the caller so this card always renders its header; when false we
  // show the standard access message instead of the table (matching the platform's section pattern).
  hasPermission: boolean
  refreshToken?: number
}) => {
  const properties = fieldAssetStore.useSelector((s) => s.properties)

  const propertyName =
    (propertyId === null ? undefined : properties?.[propertyId]?.propertyName) ??
    translate.phrases.banyanApp('Unknown Property')

  const traps = useMemo(
    () => getTrapsForProperty({ properties, propertyId, insectId }),
    [properties, propertyId, insectId],
  )

  const lngLats = useMemo(() => traps.map((t) => t.lngLat), [traps])
  const { data, loading, error } = useTrapCatchPoints({ propertyId, insectId, lngLats, refreshToken })
  const valueKey = trapCatchKeyForInsect(insectId)

  // One row per trap with its sliced counts — shared by the table and the CSV download so they
  // never diverge (the download only contains what's on screen).
  const rows = useMemo(
    () =>
      traps.map((trap) => {
        // Type the value-type key, then read by optional chaining (no `as Record<…>` on the container).
        const key =
          valueKey as keyof VV.DomainTypes.TrapCatchesInsect.TValuesReturnWithMetaIgnoringKeying['points']

        const timeseries = data?.[trap.lngLat]?.values?.[key]?.[0]?.timeseries ?? []

        return { trap, counts: sliceTrapTimeseries(timeseries) }
      }),
    [traps, data, valueKey],
  )

  const handleDownload = useCallback(() => {
    const headers = [
      translate.phrases.banyanApp('Trap Name'),
      translate.phrases.banyanApp('Block Name'),
      translate.phrases.banyanApp('Today'),
      translate.phrases.banyanApp('Past 7 Days'),
      translate.phrases.banyanApp('Past 14 Days'),
      translate.phrases.banyanApp('Year to Date'),
    ]

    const csvRows = rows.map(({ trap, counts }) => [
      trap.trapName,
      trap.blockName ?? '',
      formatCountForCsv(counts.today),
      formatCountForCsv(counts.past7Days),
      formatCountForCsv(counts.past14Days),
      formatCountForCsv(counts.yearToDate),
    ])

    const baseName = translate.phrases.banyanApp('Historical Trap Counts')
    // Stamp the export date — the counts are relative windows (Today / Past 7 / 14 / YTD), so the
    // file is only interpretable later if you know when it was taken. Localized via translate.dates,
    // matching the dashboard's "Last Updated" header.
    const today = translate.dates.format(moment.tz(moment.tz.guess()), 'MMM D, YYYY')
    // Start with the pest name and include the property + date, matching the chart's JPG export
    // pattern (`<pest> <document> - <property>`). The download is only offered when a property is
    // selected with rows, so propertyName is a real name here.
    const fileName = `${pestName} ${baseName} - ${propertyName} - ${today}.csv`

    downloadCsv(fileName, headers, csvRows)
  }, [rows, propertyName, pestName])

  // Always render the card header; show the standard access / no-data message in the body when there's
  // nothing to table (no permission, or permitted but the property has no traps for this pest).
  const getEmptyMessage = (): string | null => {
    if (!hasPermission) {
      return translate.phrases.banyanApp('You do not have access to this feature for {{propertyName}}', {
        propertyName,
      })
    }

    if (traps.length === 0) {
      return translate.phrases.banyanApp('No data available for this property {{propertyName}}', {
        propertyName,
      })
    }

    // Without this a failed fetch leaves every count null, which the table shows as a full set of
    // dashes, the same as a trap with no catches.
    if (error) return translate.phrases.banyanApp('Unable to load data. Try refreshing.')

    return null
  }

  const emptyMessage = getEmptyMessage()

  return (
    <WidgetCard
      title={translate.phrases.banyanApp('Historical Trap Counts')}
      loading={loading}
      onDownload={hasPermission && data && !loading && !error && rows.length > 0 ? handleDownload : undefined}
    >
      {emptyMessage ? (
        <DashboardMessage>{emptyMessage}</DashboardMessage>
      ) : (
        <table css={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th css={headerCss}>{translate.phrases.banyanApp('Trap / Block Name')}</th>
              <th css={todayHeaderCss}>{translate.phrases.banyanApp('Today')}</th>
              <th css={headerCss}>{translate.phrases.banyanApp('Past 7 Days')}</th>
              <th css={headerCss}>{translate.phrases.banyanApp('Past 14 Days')}</th>
              <th css={headerCss}>{translate.phrases.banyanApp('Year to Date')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ trap, counts }) => (
              <tr key={trap.lngLat}>
                <td css={cellCss}>
                  <div css={{ fontSize: 14, fontWeight: 600, color: colors.midnight }}>{trap.trapName}</div>
                  {trap.blockName && (
                    <div css={{ fontSize: 14, color: dashboardColors.mutedText, marginTop: 2 }}>
                      {trap.blockName}
                    </div>
                  )}
                </td>
                <td css={todayCellCss}>{formatCount(counts.today)}</td>
                <td css={cellCss}>{formatCount(counts.past7Days)}</td>
                <td css={cellCss}>{formatCount(counts.past14Days)}</td>
                <td css={cellCss}>{formatCount(counts.yearToDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </WidgetCard>
  )
}
